import { CSSProperties, memo, useState } from 'react';

import styled from 'styled-components';

import { IconDropdown } from '@components/icons/dropdown';
import { IconEdit } from '@components/icons/edit';
import { Menu, MenuItemProps } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { Color, Font, theme } from '@styles/theme';
import { createPersistentMeasurement } from '@utils/browser';
import { pxToRem, styleToString } from '@utils/converters';
import { MakePropertiesRequired } from '@utils/types';

export type LabelInputTextProps = {
  defaultValue?: string;
  onSave?: (newValue: string) => void;
  onIsEditingChange?: (isEditing: boolean) => void;
  font?: Font;
  color?: Color;
  label?: string;
  hideLabel?: boolean;
  maxLength?: number;
  dataTestid?: string;
  dataCy?: string;
  extraMenuItems?: Array<MakePropertiesRequired<MenuItemProps, 'id'>>;
  minWidth?: CSSProperties['minWidth'];
  maxWidth?: CSSProperties['maxWidth'];
};

const styleStr = styleToString({
  font: theme.font.regular16,
  margin: `0 ${pxToRem(2)}`,
  whiteSpace: 'pre',
});
const staticNodeStructure = `<div style="${styleStr}" data-dynamic></div>`;
const measureNodeHeight = createPersistentMeasurement(staticNodeStructure);

export const LabelInputText = memo((props: LabelInputTextProps) => {
  const {
    defaultValue = '',
    label = 'title',
    hideLabel = false,
    font = 'regular16',
    color = 'textPrimary',
    onSave,
    onIsEditingChange,
    maxLength,
    dataTestid,
    dataCy,
    extraMenuItems = [],
    minWidth = pxToRem(120),
    maxWidth = pxToRem(512),
  } = props;
  const { t } = useScopedTranslate(locale);
  const [value, setValue] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const updateIsEditing = (newIsEditing: boolean) => {
    setIsEditing(newIsEditing);
    onIsEditingChange?.(newIsEditing);
  };
  const startEditing = () => updateIsEditing(true);
  const stopEditing = () => updateIsEditing(false);

  const handleSubmit = async () => {
    await onSave?.(value);
    stopEditing();
  };

  const { width } = measureNodeHeight(value);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
    } else if (event.key === 'Escape') {
      setValue(defaultValue);
      stopEditing();
    }
  };

  const menuItems = [
    ...extraMenuItems,
    {
      id: 'rename',
      dataCy: 'rename',
      label: t('rename'),
      icon: <IconEdit />,
      onSelect: startEditing,
    },
  ];

  return (
    <Stack orientation="horizontal" align="center" justify="center" className="label-input-text">
      {isEditing ? (
        <TextField
          maxLength={maxLength}
          autoFocus
          label={label}
          hideLabel={hideLabel}
          value={value}
          onChange={setValue}
          onKeyDown={handleKeyPress}
          dataTestid={dataTestid}
          onBlur={handleSubmit}
          style={{ minWidth, maxWidth, width }}
        />
      ) : (
        <>
          <Tooltip content={value}>
            <div onClick={startEditing} data-testid={dataTestid}>
              <StyledText text={value} font={font} color={color} />
            </div>
          </Tooltip>
          <Menu
            buttonProps={{
              dataCy,
              iconLeft: <IconDropdown color="purple50" />,
              variant: 'ghost-no-background',
            }}
            placement="bottom-start"
          >
            {menuItems.map((menuItem) => (
              <Menu.Item key={menuItem.id} {...menuItem} />
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
});

const StyledText = styled(Text)({
  cursor: 'pointer',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.color.elementBlue100,
  },
});

const locale = {
  en: {
    rename: 'Rename',
  },
};
